import { LitElement, html } from './-global.min.js'

class HHProductCard extends LitElement {
  constructor() {
    super()
    this.productUrl = ''
    this.options = []
    this.currentVariantData = {}
    this.currentVariantSupplementData = {}
    this.collapseOpenState = false
  }

  get variantData() {
    return JSON.parse(this.querySelector('.js-card-variant-data').textContent)
  }

  get variantSupplementData() {
    return JSON.parse(
      this.querySelector('.js-card-variant-supplement-data').textContent
    )
  }

  get anchor() {
    return this.querySelector('.js-anchor')
  }

  get variantButtons() {
    return [...this.querySelectorAll('.js-swatch')]
  }

  get quickBuyButton() {
    return this.querySelector('.js-card-quick-add')
  }

  get quickBuyIdInput() {
    return this.querySelector('.js-quick-buy-id')
  }

  get cardPriceWrapper() {
    return this.querySelector('.js-price-wrapper')
  }

  get cardPrice() {
    return this.querySelectorAll('.js-price')
  }

  get cardComparePrice() {
    return this.querySelectorAll('.js-compare-price')
  }

  get cardBadge() {
    return this.querySelector('.js-card-badge')
  }

  get cardInfoWrapper() {
    return this.querySelector('.js-card-info-wrapper')
  }

  get cardCta() {
    return this.querySelector('.js-card-cta')
  }

  get collapse() {
    return this.querySelector('.js-card-collapse')
  }

  get collapseOpenButton() {
    return this.querySelector('.js-card-collapse-open')
  }

  get collapseCloseButton() {
    return this.querySelector('.js-card-collapse-close')
  }

  get fieldset() {
    return this.querySelector('.js-card-fieldset')
  }

  get variantMedias() {
    return this.querySelectorAll('.js-variant-media')
  }

  get cardPriceWrappers() {
    return this.querySelectorAll('.js-price-wrapper')
  }

  get cardPrices() {
    return this.querySelectorAll('.js-price')
  }

  get cardComparePrices() {
    return this.querySelectorAll('.js-compare-price')
  }

  get quickShopButton() {
    return this.querySelector('.js-card-quick-shop-button')
  }

  get formIdInput() {
    return this.querySelector('.js-quick-buy-id')
  }

  get colorSwatchLabel() {
    return this.querySelector('.js-color-selected-value')
  }

  connectedCallback() {
    super.connectedCallback()
    this.variantButtons.forEach((button) => {
      button.addEventListener('click', this.onClickHandler.bind(this))
    })

    if (this.fieldset)
      this.fieldset.addEventListener('change', this.onChangeHandler.bind(this))

    if (this.collapse) {
      if (this.collapseOpenButton)
        this.collapseOpenButton.addEventListener('click', () =>
          this.collapseOpen()
        )
      if (this.collapseCloseButton)
        this.collapseCloseButton.addEventListener('click', () =>
          this.collapseClose()
        )
      this.addEventListener('mouseenter', () => this.handleDesktopOpen())
      this.addEventListener('focusin', () => this.handleDesktopOpen())
      window.addEventListener(
        'hh-ds-collapse-open',
        this.handleCollapseEvent.bind(this)
      )
      window.addEventListener('resize', () => this.queryCollapseHeight())
      window.addEventListener('resize', () => this.queryCardInfoHeight())
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.variantButtons.forEach((button) => {
      button.removeEventListener('click', this.onClickHandler)
    })

    if (this.fieldset)
      this.fieldset.removeEventListener('change', this.onChangeHandler)
    if (this.collapse) {
      if (this.collapseOpenButton)
        this.collapseOpenButton.removeEventListener('click', () =>
          this.collapseOpen()
        )
      if (this.collapseCloseButton)
        this.collapseCloseButton.removeEventListener('click', () =>
          this.collapseClose()
        )
      this.removeEventListener('mouseenter', () => this.handleDesktopOpen())
      this.removeEventListener('focusin', () => this.handleDesktopOpen())
      window.removeEventListener(
        'hh-ds-collapse-open',
        this.handleCollapseEvent
      )
      window.removeEventListener('resize', () => this.queryCollapseHeight())
      window.removeEventListener('resize', () => this.queryCardInfoHeight())
    }
  }

  firstUpdated() {
    if (this.cardInfoWrapper && this.cardCta) {
      this.cardCta.style.setProperty(
        '--card-cta-hover-bottom',
        `${this.cardInfoWrapper.offsetHeight}px`
      )
    }
  }

  async onClickHandler(event) {
    event.preventDefault()
    this.anchor.href = event.target.dataset.variantUrl
    this.hideAllVariantImages()
    this.showVariantImage(event)
    this.updateQuickBuyId(event)
    this.updateQuickBuyButton(event)
    this.updateCardPrice(event)
    this.updateBadge(event)
    this.variantButtons.forEach((el) => {
      el.classList.remove('active')
    })

    event.target.classList.add('active')
  }

  async hideAllVariantImages() {
    this.querySelectorAll('[data-variant-color]').forEach((element) => {
      element.classList.remove('active')
    })
  }

  async showVariantImage(event) {
    const vname = event.target.dataset.variantName
    const selector = '[data-variant-color="' + vname + '"]'
    const swatch = this.querySelector(selector)
    swatch?.classList.add('active')
  }

  updateQuickBuyButton(event) {
    if (!this.quickBuyButton) return
    const variantAvailable = event.target.dataset.variantAvailable
    if (variantAvailable === 'true') {
      this.quickBuyButton.removeAttribute('disabled')
      this.quickBuyButton.innerHTML = `${window.variantStrings.addToCart}`
    } else {
      this.quickBuyButton.setAttribute('disabled', 'disabled')
      this.quickBuyButton.innerHTML = window.variantStrings.soldOut
    }
  }

  updateQuickBuyId(event) {
    if (!this.quickBuyIdInput) return
    this.quickBuyIdInput.value = event.target.dataset.variantId
  }

  updateCardPrice(event) {
    if (!this.cardPriceWrapper) return

    const isCompareAtPriceInvalid =
      !event.target.dataset.variantCompareAtPrice ||
      event.target.dataset.variantCompareAtPrice === '' ||
      event.target.dataset.variantCompareAtPrice === '$0.00'

    const isVariantUnavailable =
      event.target.dataset.variantAvailable !== 'true'

    if (isCompareAtPriceInvalid || isVariantUnavailable) {
      this.cardPriceWrapper.classList.remove('price--on-sale')
    } else {
      this.cardPriceWrapper.classList.add('price--on-sale')
    }

    for (const priceEl of this.cardPrice) {
      priceEl.innerHTML = event.target.dataset.variantPrice
    }

    for (const comparePriceEl of this.cardComparePrice) {
      if (
        !event.target.dataset.variantCompareAtPrice ||
        event.target.dataset.variantCompareAtPrice === '' ||
        event.target.dataset.variantCompareAtPrice === '$0.00'
      ) {
        comparePriceEl.innerHTML = ''
      } else {
        comparePriceEl.innerHTML = event.target.dataset.variantCompareAtPrice
      }
    }
  }

  updateBadge(event) {
    if (
      !this.cardBadge ||
      !this.cardBadge.classList.contains('js-variant-level-badge')
    )
      return
    if (!event.target.dataset.variantBadge) {
      this.cardBadge.innerHTML = ''
      this.cardBadge.style.backgroundColor = ''
      this.cardBadge.style.color = ''
      return
    }
    const badgeData = JSON.parse(event.target.dataset.variantBadge)
    this.cardBadge.innerHTML = badgeData.text
    this.cardBadge.style.backgroundColor = badgeData.background_color
    this.cardBadge.style.color = badgeData.font_color
  }

  queryCollapseHeight() {
    const collapseHeight = this.collapse.offsetHeight
    this.style.setProperty('--collapse-height', `${collapseHeight * 2}px`)
  }

  queryCardInfoHeight() {
    const cardInfoHeight = this.cardInfo.offsetHeight
    this.style.setProperty('--card-info-height', `${cardInfoHeight}px`)
  }

  collapseOpen() {
    this.collapse.classList.remove('js-hidden')
    this.collapseOpenState = true

    const event = new CustomEvent('hh-ds-collapse-open', {
      detail: { initiator: this },
      bubbles: true,
      composed: true,
    })
    window.dispatchEvent(event)
  }

  collapseClose() {
    if (!this.collapse.classList.contains('js-hidden'))
      this.collapse.classList.add('js-hidden')
    this.collapseOpenState = false
  }

  handleDesktopOpen() {
    if (window.innerWidth > 990 && !this.collapseOpenState) {
      this.collapseOpen()
    }
  }

  handleCollapseEvent(event) {
    if (event.detail.initiator !== this) {
      this.collapseClose()
    }
  }

  onChangeHandler(event) {
    this.querySelectedOptions()
    this.querySelectedVariant()
    this.updateURL()
    this.updateActiveMedia()
    this.updateFormId()
    this.updateQuickShopButton()
    this.updateBadge()
    this.updateColorSwatchLabel(event)
    this.updateProductCardPrice()
  }

  querySelectedOptions() {
    const arrayFieldset = Array.from(this.querySelectorAll('fieldset'))
    this.options = arrayFieldset.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find(
        (radio) => radio.checked
      ).value
    })
  }

  querySelectedVariant() {
    if (!this.variantData || !Array.isArray(this.variantData)) {
      console.error('Variant data is not available or invalid.')
      return
    }

    this.currentVariantData = this.variantData.find((variant) => {
      return !variant.options
        .map((option, index) => this.options[index] === option)
        .includes(false)
    })

    if (!this.currentVariantData) {
      console.warn('No matching variant found.')
      return
    }

    this.currentVariantSupplementData = this.variantSupplementData?.find(
      (variant) => variant.id === this.currentVariantData.id
    )
  }

  updateURL() {
    if (this.currentVariantSupplementData && this.anchor) {
      this.anchor.href = this.currentVariantSupplementData.url
    }
  }

  updateFormId() {
    if (this.currentVariantData && this.formIdInput) {
      this.formIdInput.value = this.currentVariantData.id
    }
  }

  updateQuickShopButton() {
    if (this.currentVariantData && this.quickShopButton) {
      const variantAvailable = this.currentVariantData.available
      const variantPrice = (this.currentVariantData.price / 100).toFixed(2)
      if (variantAvailable) {
        this.quickShopButton.removeAttribute('disabled')
        this.quickShopButton.innerHTML = `${window.variantStrings.addToCart}`
      } else {
        this.quickShopButton.setAttribute('disabled', 'disabled')
        this.quickShopButton.innerHTML = window.variantStrings.soldOut
      }
    }
  }

  updateBadge() {
    if (
      this.badge &&
      this.badge.classList.contains('js-variant-level-badge') &&
      this.currentVariantSupplementData
    ) {
      const badgeData = this.currentVariantSupplementData.badge
      if (
        !badgeData ||
        !badgeData.text ||
        !badgeData.background_color ||
        !badgeData.font_color
      ) {
        this.badge.innerHTML = ''
        this.badge.style.backgroundColor = ''
        this.badge.style.color = ''
        return
      }
      this.badge.innerHTML = badgeData.text
      this.badge.style.backgroundColor = badgeData.background_color
      this.badge.style.color = badgeData.font_color
    }
  }

  updateColorSwatchLabel(event) {
    if (
      this.colorSwatchLabel &&
      event.target.dataset.colorTypeValue !== 'false'
    ) {
      this.colorSwatchLabel.innerHTML = event.target.dataset.colorTypeValue
    }
  }

  updateProductCardPrice() {
    const isCompareAtPriceInvalid =
      !this.currentVariantData.compare_at_price ||
      this.currentVariantData.compare_at_price === '0' ||
      this.currentVariantData.compare_at_price === 0 ||
      this.currentVariantData.compare_at_price === ''

    const isVariantUnavailable = this.currentVariantData.available === false

    if (isCompareAtPriceInvalid || isVariantUnavailable) {
      for (const priceWrapper of this.cardPriceWrappers) {
        priceWrapper.classList.remove('price--on-sale')
      }
    } else {
      for (const priceWrapper of this.cardPriceWrappers) {
        !priceWrapper.classList.contains('price--on-sale')
          ? priceWrapper.classList.add('price--on-sale')
          : null
      }
    }

    for (const priceEl of this.cardPrices) {
      priceEl.innerHTML = `$${(this.currentVariantData.price / 100).toFixed(2)}`
    }

    for (const comparePriceEl of this.cardComparePrices) {
      if (isCompareAtPriceInvalid) {
        comparePriceEl.innerHTML = ''
      } else {
        comparePriceEl.innerHTML = `$${(
          this.currentVariantData.compare_at_price / 100
        ).toFixed(2)}`
      }
    }
  }

  updateActiveMedia() {
    for (const media of this.variantMedias) {
      media.classList.remove('active')
    }

    const selectedVariantMedia = Array.from(this.variantMedias).find(
      (media) => {
        return (
          Number(media.dataset.variantId) === Number(this.currentVariantData.id)
        )
      }
    )
    selectedVariantMedia?.classList.add('active')
  }

  render() {
    return html`<slot></slot>`
  }
}

customElements.define('hh-product-card', HHProductCard)
